import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import JobCard from './JobCard';
import styles from './JobCardsCarousel.module.scss';
import Loader from 'components/Loader/Loader';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const getCardsPerPage = () => {
  if (typeof window === 'undefined') return 3;
  const width = window.innerWidth;
  if (width < 768) return 1;
  if (width < 1024) return 2;
  return 3;
};

const JobCardsCarousel = ({ briefs, loadingAllBriefs }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState(null);
  const [nextPage, setNextPage] = useState(null);
  const [cardsPerPage, setCardsPerPage] = useState(getCardsPerPage());
  const totalPages = Math.ceil(briefs.length / cardsPerPage);

  useEffect(() => {
    const handleResize = () => {
      const newCardsPerPage = getCardsPerPage();
      if (newCardsPerPage !== cardsPerPage) {
        setCardsPerPage(newCardsPerPage);
        setCurrentPage(0);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [cardsPerPage]);

  useEffect(() => {
    if (isAnimating && nextPage !== null) {
      const timer = setTimeout(() => {
        setCurrentPage(nextPage);
        setIsAnimating(false);
        setNextPage(null);
        setDirection(null);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isAnimating, nextPage]);

  const handlePrevious = () => {
    if (isAnimating) return;
    const pages = Math.ceil(briefs.length / cardsPerPage);
    const newPage = currentPage === 0 ? pages - 1 : currentPage - 1;
    setDirection('left');
    setNextPage(newPage);
    setIsAnimating(true);
  };

  const handleNext = () => {
    if (isAnimating) return;
    const pages = Math.ceil(briefs.length / cardsPerPage);
    const newPage = currentPage === pages - 1 ? 0 : currentPage + 1;
    setDirection('right');
    setNextPage(newPage);
    setIsAnimating(true);
  };

  const handleDotClick = (pageIndex) => {
    if (isAnimating || pageIndex === currentPage) return;
    setDirection(pageIndex > currentPage ? 'right' : 'left');
    setNextPage(pageIndex);
    setIsAnimating(true);
  };

  const getCurrentBriefs = (page) => {
    const startIndex = page * cardsPerPage;
    const endIndex = startIndex + cardsPerPage;
    return briefs.slice(startIndex, endIndex);
  };

  const currentBriefs = getCurrentBriefs(currentPage);
  const nextBriefs = nextPage !== null ? getCurrentBriefs(nextPage) : null;
  const shouldShowCarousel = briefs.length > cardsPerPage;

  if (loadingAllBriefs) {
    return <Loader loading />;
  }

  return (
    <div className={styles.carouselContainer}>
      <div className={styles.carouselNavRow}>
        {shouldShowCarousel && (
          <button
            className={styles.navButton}
            onClick={handlePrevious}
            aria-label="Previous page"
          >
            <ChevronLeft width={24} height={24} />
          </button>
        )}

        <div className={styles.cardsWrapper}>
          <div className={styles.carouselTrack}>
            <div
              className={`${styles.jobCardsGrid} ${styles.currentSlide} ${
                isAnimating
                  ? direction === 'right'
                    ? styles.slideOutLeft
                    : styles.slideOutRight
                  : ''
              }`}
            >
              {currentBriefs.map((brief) => (
                <JobCard key={brief.id} brief={brief} />
              ))}
            </div>
            {isAnimating && nextBriefs && (
              <div
                className={`${styles.jobCardsGrid} ${styles.nextSlide} ${
                  direction === 'right'
                    ? styles.slideInRight
                    : styles.slideInLeft
                }`}
              >
                {nextBriefs.map((brief) => (
                  <JobCard key={`next-${brief.id}`} brief={brief} />
                ))}
              </div>
            )}
          </div>
        </div>

        {shouldShowCarousel && (
          <button
            className={styles.navButton}
            onClick={handleNext}
            aria-label="Next page"
          >
            <ChevronRight width={24} height={24} />
          </button>
        )}
      </div>

      {shouldShowCarousel && (
        <div className={styles.pagination}>
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              className={`${styles.paginationDot} ${
                index ===
                (isAnimating && nextPage !== null ? nextPage : currentPage)
                  ? styles.paginationDotActive
                  : ''
              }`}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

JobCardsCarousel.propTypes = {
  briefs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      text: PropTypes.string,
      description: PropTypes.string,
      timestamp: PropTypes.string,
      stats: PropTypes.shape({
        invited: PropTypes.number,
        applied: PropTypes.number,
        reviewed: PropTypes.number,
        shortlisted: PropTypes.number,
        declined: PropTypes.number,
        hired: PropTypes.number,
      }),
    })
  ).isRequired,
  loadingAllBriefs: PropTypes.bool.isRequired,
};

export default JobCardsCarousel;
